/** @format */

import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Spin } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { useDispatch } from "react-redux";
import { API_ENDPOINTS } from "../../../../constants/api";
import { actionPresenceCreateRequest } from "../../../../redux-modules/actions/actionsPresences";
import { TPresenceType } from "../../../../redux-modules/declarations/maps/presenceTypes";
import { TPresence } from "../../../../redux-modules/declarations/maps/presences";
import apiClient from "../../../../utils/apiClient";
import { messaggioErrore } from "../../ViewPresenze/usePresenze";
import { TimbraturaStile } from "../styles";

/** Durata del codice QR prima della rigenerazione automatica, come nella Home precedente. */
const DURATA_QR = 300000;

/** Turno timbrabile restituito da GET /shift_plannings/next (id null = turno non programmato). */
interface TTurnoTimbrabile {
  id: string | null;
  orario: string;
  sede: string | null;
}

interface Props {
  visibile: boolean;
  /** timbratura dal web (utente con is_presence_in_app), altrimenti codice QR per l'iPad */
  inApp: boolean;
  timebankAbilitata: boolean;
  userId: string;
  tipiPresenza: TPresenceType[];
  /** presenza aperta: con il QR la finestra mostra subito il codice per chiuderla */
  aperta: TPresence | null;
  onChiudi: () => void;
}

/**
 * Finestra di timbratura. Stesse chiamate della Home precedente:
 *  - dal web: POST /presences (actionPresenceCreateRequest) con origin "web", senza device_code (la presenza si salva senza dispositivo, nella sede dell'utente);
 *  - con il QR: codice con user_id, shift_planning_id, generate_date e presence_type_id, rigenerato ogni 5 minuti.
 * I turni proposti sono quelli di GET /shift_plannings/next, letti all'apertura della finestra.
 */
export const Timbratura = ({
  visibile,
  inApp,
  timebankAbilitata,
  userId,
  tipiPresenza,
  aperta,
  onChiudi,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const visibili = tipiPresenza.filter(t => !!t.is_visible);
  const [tipo, setTipo] = useState<TPresenceType | undefined>();
  const [turni, setTurni] = useState<TTurnoTimbrabile[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  // undefined: non ancora scelto; null: turno non programmato
  const [turnoScelto, setTurnoScelto] = useState<string | null | undefined>(undefined);
  const [qr, setQr] = useState("");
  const [scadenza, setScadenza] = useState(0);
  const [adesso, setAdesso] = useState(Date.now());

  const chiusuraQr = !inApp && !!aperta;

  // all'apertura: tipo della presenza aperta (chiusura con QR) o unico tipo visibile, e turni timbrabili
  useEffect(() => {
    if (!visibile) return;
    setTurnoScelto(undefined);
    setQr("");
    setErrore(null);
    if (chiusuraQr && aperta) {
      setTipo(tipiPresenza.find(t => String(t.id) === String(aperta.presence_type_id)));
    } else {
      setTipo(visibili.length === 1 ? visibili[0] : undefined);
    }
    if (chiusuraQr) return;
    setTurni(null);
    apiClient
      .get(API_ENDPOINTS.SHIFTS_NEXT + "?version=2&device=web")
      .then(r => setTurni((r.data?.today || []) as TTurnoTimbrabile[]))
      .catch(err => {
        setTurni([]);
        setErrore(messaggioErrore(err, "Impossibile leggere i turni di oggi"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibile]);

  const sceltaTurno =
    !inApp &&
    !chiusuraQr &&
    timebankAbilitata &&
    turnoScelto === undefined &&
    (turni || []).length > 0;
  const mostraQr = !inApp && !!tipo && (chiusuraQr || (turni !== null && !sceltaTurno));

  const generaQr = (): void => {
    if (!tipo) return;
    const valore = {
      user_id: userId,
      shift_planning_id: turnoScelto,
      generate_date: moment().format("YYYY-MM-DD HH:mm:ss"),
      presence_type_id: tipo.id,
    };
    setQr(JSON.stringify(valore));
    setAdesso(Date.now());
    setScadenza(Date.now() + DURATA_QR);
  };

  // codice nuovo quando si arriva al QR o cambia il turno; poi ogni 5 minuti
  useEffect(() => {
    if (visibile && mostraQr) generaQr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibile, mostraQr, tipo, turnoScelto]);

  useEffect(() => {
    if (!visibile || !mostraQr) return undefined;
    const timer = window.setInterval(() => setAdesso(Date.now()), 1000);
    return (): void => window.clearInterval(timer);
  }, [visibile, mostraQr]);

  useEffect(() => {
    if (mostraQr && qr && adesso >= scadenza) generaQr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adesso]);

  const timbraDalWeb = (shiftPlanningId: string | null): void => {
    if (!tipo) return;
    dispatch(
      actionPresenceCreateRequest({
        generate_date: moment().format("YYYY-MM-DD HH:mm:ss"),
        user_id: userId,
        shift_planning_id: shiftPlanningId,
        presence_type_id: tipo.id,
        origin: "web",
      }),
    );
    onChiudi();
  };

  const secondi = Math.ceil(Math.max(0, scadenza - adesso) / 1000);
  const restante = `${String(Math.floor(secondi / 60)).padStart(2, "0")}:${String(
    secondi % 60,
  ).padStart(2, "0")}`;
  const titolo = chiusuraQr ? "Chiudi la timbratura" : "Nuova timbratura";

  return (
    <Modal
      visible={visibile}
      title={titolo}
      onCancel={onChiudi}
      footer={[
        <Button key="chiudi" onClick={onChiudi}>
          {mostraQr ? "Fatto" : "Annulla"}
        </Button>,
      ]}
      destroyOnClose
      width={480}
    >
      <TimbraturaStile>
        {errore && <Alert type="warning" showIcon message={errore} />}

        {!chiusuraQr && (
          <div>
            <div className="passo" id="passo-tipo">
              Tipo di presenza
            </div>
            <div
              className="tipi"
              role="group"
              aria-labelledby="passo-tipo"
              style={{ marginTop: 8 }}
            >
              {visibili.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className="tipo"
                  aria-pressed={tipo?.id === t.id}
                  onClick={(): void => {
                    setTipo(t);
                    setTurnoScelto(undefined);
                  }}
                >
                  <i style={{ background: t.color }} aria-hidden="true" />
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {tipo && !chiusuraQr && turni === null && <Spin tip="Lettura dei turni di oggi" />}

        {/* dal web: un pulsante per ogni turno timbrabile, come la Home precedente */}
        {tipo && inApp && turni !== null && (
          <div className="turni">
            <div className="passo">Quale turno stai iniziando?</div>
            {turni.length > 0 ? (
              turni.map(t => (
                <button
                  key={String(t.id)}
                  type="button"
                  className="turno-scelta"
                  onClick={(): void => timbraDalWeb(t.id)}
                >
                  {t.id === null ? "Turno non programmato" : `Apri il turno ${t.orario}`}
                  {t.sede && <span className="sede">{t.sede}</span>}
                </button>
              ))
            ) : (
              <button
                type="button"
                className="turno-scelta"
                onClick={(): void => timbraDalWeb(null)}
              >
                Apri il turno
              </button>
            )}
          </div>
        )}

        {/* QR con la banca ore: prima si sceglie il turno */}
        {tipo && sceltaTurno && (
          <div className="turni">
            <div className="passo">Seleziona il turno da effettuare</div>
            {(turni || []).map(t => (
              <button
                key={String(t.id)}
                type="button"
                className="turno-scelta"
                onClick={(): void => setTurnoScelto(t.id)}
              >
                {t.id === null ? "Turno non programmato" : t.orario}
                {t.sede && <span className="sede">{t.sede}</span>}
              </button>
            ))}
          </div>
        )}

        {mostraQr && tipo && qr && (
          <div className="qr">
            <span className="titolo" style={{ color: tipo.color }}>
              {tipo.name}
            </span>
            <QRCode value={qr} eyeRadius={5} size={190} />
            <span className="conto numeri" aria-live="polite">
              Valido ancora {restante}
            </span>
            <Button icon={<ReloadOutlined />} onClick={generaQr}>
              Rigenera
            </Button>
            <span className="nota">
              Inquadra il codice con il tablet della sede. La pagina si aggiorna da sola quando la
              timbratura arriva.
            </span>
          </div>
        )}

        {!chiusuraQr && visibili.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="Nessun tipo di presenza disponibile per la timbratura"
          />
        )}
      </TimbraturaStile>
    </Modal>
  );
};
